import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export default function DataTable({
  columns, data, searchKeys = [], pageSize = 10, emptyText = "Belum ada data", testId = "data-table",
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [search]);

  const filtered = !search
    ? data
    : data.filter((row) =>
        searchKeys.some((k) => String(row[k] ?? "").toLowerCase().includes(search.toLowerCase()))
      );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const slice = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div data-testid={testId}>
      {searchKeys.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <div className="relative w-72">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cari…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="text-xs text-muted-foreground ml-auto mono">
            {filtered.length} baris
          </div>
        </div>
      )}

      <div className="border border-border rounded-md overflow-hidden bg-card">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key || c.header} className={c.align === "right" ? "text-right" : ""}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted-foreground py-8">{emptyText}</td>
              </tr>
            ) : (
              slice.map((row, ri) => {
                const rowKey =
                  row.id ||
                  row.borrower_id || row.facility_id || row.group_id || row.exposure_id ||
                  row.collateral_id || row.guarantor_id || row.covenant_id || row.approval_id ||
                  row.alert_id || row.shareholder_id ||
                  (row.borrower_a_id ? `${row.borrower_a_id}-${row.borrower_b_id}-${row.connection_type}-${row.via}` : null) ||
                  `row-${(page - 1) * pageSize + ri}`;
                return (
                  <tr key={rowKey} data-testid={`${testId}-row-${ri}`}>
                    {columns.map((c) => (
                      <td key={c.key || c.header} className={c.align === "right" ? "text-right-num" : c.cellClassName || ""}>
                        {c.cell ? c.cell(row) : row[c.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <span className="text-muted-foreground mono">Hal {page}/{totalPages}</span>
          <Button
            variant="outline" size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Button
            variant="outline" size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
